import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

/**
 * Single source of truth for auth + participant. One getSession call, one
 * onAuthStateChange listener, shared across the whole app via context.
 *
 * Wrap the app in <ParticipantProvider>. Use useParticipant() to read.
 *
 * status: 'loading' | 'ready'
 */
const Ctx = createContext({ participant: null, session: null, status: 'loading' })

export function ParticipantProvider({ children }) {
  const [state, setState] = useState({ participant: null, session: null, status: 'loading' })

  useEffect(() => {
    if (!supabase) {
      setState({ participant: null, session: null, status: 'ready' })
      return
    }

    let cancelled = false

    async function load(session) {
      if (!session?.user) {
        if (!cancelled) setState({ participant: null, session: null, status: 'ready' })
        return
      }
      try {
        const { data, error } = await supabase
          .from('participants')
          .select('*')
          .eq('email', session.user.email.toLowerCase())
          .maybeSingle()
        if (cancelled) return
        if (error) {
          console.error('Failed to load participant:', error)
          setState({ participant: null, session, status: 'ready' })
          return
        }
        setState({ participant: data, session, status: 'ready' })

        // Fire the "X joined the league" Teams card on first sign-in only.
        // The function is idempotent so re-fires are safe.
        if (data && !data.first_signed_in_at) {
          fetch('/.netlify/functions/notify-first-signin', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}` },
          }).catch(err => console.warn('notify-first-signin failed:', err))
        }
      } catch (err) {
        console.error('participant load threw:', err)
        if (!cancelled) setState({ participant: null, session, status: 'ready' })
      }
    }

    supabase.auth.getSession().then(({ data }) => load(data.session))

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      load(session)
    })

    return () => {
      cancelled = true
      sub.subscription?.unsubscribe()
    }
  }, [])

  const value = useMemo(() => state, [state])
  return createElement(Ctx.Provider, { value }, children)
}

export function useParticipant() {
  return useContext(Ctx)
}

export async function signOut() {
  if (!supabase) return
  await supabase.auth.signOut()
  window.location.href = '/welcome'
}
